import React, { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  REQUEST_SETUP_MFA_AUTHENTICATOR_TOTP,
  CONFIRM_MFA_AUTHENTICATOR_TOTP,
  RequestSetupMfaAuthenticatorTotpResponse,
} from '../graphql/mfa';
import { otpCode6Schema } from '../schemas/mfaValidation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPGroupComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPSlotComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QRCodeComponentType = React.ComponentType<any>;

export interface MfaAuthenticatorSetupProps {
  userEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
  DialogComponent: DialogComponentType;
  ButtonComponent: ButtonComponentType;
  InputOTPComponent: InputOTPComponentType;
  InputOTPGroupComponent: InputOTPGroupComponentType;
  InputOTPSlotComponent: InputOTPSlotComponentType;
  QRCodeComponent: QRCodeComponentType;
}

type SetupStep = 'initial' | 'qrcode' | 'confirm';

export function MfaAuthenticatorSetup({
  userEmail,
  onSuccess,
  onCancel,
  DialogComponent,
  ButtonComponent,
  InputOTPComponent,
  InputOTPGroupComponent,
  InputOTPSlotComponent,
  QRCodeComponent,
}: MfaAuthenticatorSetupProps) {
  const client = useApolloClient();
  const [step, setStep] = useState<SetupStep>('initial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [provisionRequestId, setProvisionRequestId] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const handleStartSetup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await client.mutate({
        mutation: REQUEST_SETUP_MFA_AUTHENTICATOR_TOTP,
      })) as { data?: RequestSetupMfaAuthenticatorTotpResponse };
      const data =
        result.data?.requestSetupMfaAuthenticatorTotpForMe.mfaAuthenticatorTotpProvisionRequest;
      if (data) {
        setSecretKey(data.totpSecretKey);
        setProvisionRequestId(data.id);
        setStep('qrcode');
      }
    } catch (err) {
      setError('Failed to start authenticator setup. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [client]);

  const handleConfirm = useCallback(async () => {
    if (!provisionRequestId) return;

    const result = otpCode6Schema.safeParse({ code });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await client.mutate({
        mutation: CONFIRM_MFA_AUTHENTICATOR_TOTP,
        variables: {
          id: provisionRequestId,
          input: { auth_code: code },
        },
      });
      onSuccess();
    } catch (err) {
      setError('Invalid code. Please check and try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [client, provisionRequestId, code, onSuccess]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        onCancel();
      }
    },
    [onCancel]
  );

  const otpauthUrl = secretKey
    ? `otpauth://totp/Swift SMS Gateway:${userEmail}?secret=${secretKey}&issuer=Swift SMS Gateway`
    : '';

  return (
    <DialogComponent
      isOpen={true}
      setIsOpen={handleClose}
      title="Set up Authenticator App"
      centered
    >
      <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
        {error && (
          <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        {step === 'initial' && (
          <>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full dark:bg-blue-900">
                  <span className="text-3xl text-blue-600 material-symbols-outlined dark:text-blue-400">
                    smartphone
                  </span>
                </div>
              </div>
              <p className="text-grey-600 dark:text-grey-300">
                Use an authenticator app like Google Authenticator, Authy, or 1Password to generate
                verification codes for your account.
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleStartSetup}
                loading={loading}
                disabled={loading}
              >
                Continue
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </ButtonComponent>
            </div>
          </>
        )}

        {step === 'qrcode' && secretKey && (
          <>
            <div className="text-center">
              <p className="mb-4 md:text-base text-sm text-grey-600 dark:text-grey-300">
                Scan this QR code with your authenticator app, or enter the secret key manually.
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg">
              <QRCodeComponent value={otpauthUrl} className="w-48 h-48" />
            </div>
            <div className="w-full text-center">
              <p className="mb-2 text-sm text-grey-500 dark:text-grey-400">
                Or enter this key manually:
              </p>
              <div className="flex gap-2 justify-center items-center p-3 rounded-lg bg-grey-100 dark:bg-grey-800">
                <code className="font-mono text-sm text-center break-all select-all">
                  {secretKey}
                </code>
              </div>
            </div>
            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent type="button" onClick={() => setStep('confirm')}>
                Next
              </ButtonComponent>
              <ButtonComponent type="button" variant="tertiary" onClick={onCancel}>
                Cancel
              </ButtonComponent>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="text-center">
              <p className="text-grey-600 dark:text-grey-300">
                Enter the 6-digit code from your authenticator app to verify setup.
              </p>
            </div>
            <InputOTPComponent value={code} onChange={setCode} maxLength={6} disabled={loading}>
              <InputOTPGroupComponent>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlotComponent key={i} index={i} />
                ))}
              </InputOTPGroupComponent>
            </InputOTPComponent>
            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleConfirm}
                disabled={code.length !== 6 || loading}
                loading={loading}
              >
                Verify and Enable
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={() => {
                  setStep('qrcode');
                  setCode('');
                  setError(null);
                }}
                disabled={loading}
              >
                Back
              </ButtonComponent>
            </div>
          </>
        )}
      </div>
    </DialogComponent>
  );
}
